import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Icon } from '@ng-unifi/ui';
import { useAuth } from '../auth/AuthContext.js';
import { useTenantScope } from '../auth/TenantScopeContext.js';
import { LoadingState } from '../components/LoadingState.js';
import { SearchInput } from '../components/SearchInput.js';
import { apiClient, ApiError, type ApiRequestInit } from '../lib/api.js';
import { getAllEmployees } from '../lib/employees.js';

interface TenantOption {
  id: string;
  name: string;
}

interface EmployeeOption {
  id: string;
  tenant_id?: string | null;
  tenant_name?: string | null;
  name: string;
  email: string;
  status: string;
  unifi_user_id: string | null;
}

interface JourneyEvent {
  id: string;
  type: string;
  occurred_at: string;
  severity: string;
  door_name?: string | null;
  unifi_device_id: string | null;
  unifi_door_id?: string | null;
  tenant_id?: string | null;
  employee?: EmployeeOption | null;
  snapshot: {
    id: string;
    content_type: string;
    bytes_size: number;
  } | null;
}

type RangePreset = '24h' | '7d' | '30d' | 'all' | 'custom';
type SortOrder = 'newest' | 'oldest';
type CameraFilter = 'all' | 'captured' | 'missing';
type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';
const ALL_EMPLOYEES = '__all_employees__';

function tenantInit(tenantId: string | null, skip: boolean): ApiRequestInit | undefined {
  if (!tenantId) return skip ? { skipTenantScope: true } : undefined;
  return { skipTenantScope: true, headers: { 'x-tenant-context': tenantId } };
}

function snapshotSrc(snapshotId: string, tenantId: string | null, isMaster: boolean): string {
  const suffix = isMaster && tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
  return `/api/v1/snapshots/${snapshotId}${suffix}`;
}

function dateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function startOfLocalDate(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function endOfLocalDate(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

function eventLabel(type: string): string {
  const known = new Map<string, string>([
    ['access.authorized', 'Access granted'],
    ['access.denied', 'Access denied'],
    ['access.denied.invalid_credential', 'Credential denied'],
    ['access.denied.no_permission', 'Access denied'],
    ['door.unlock', 'Door unlocked'],
    ['door.lock', 'Door locked'],
    ['door.opened', 'Door opened'],
    ['door.closed', 'Door closed'],
    ['door.forced_open', 'Forced door open'],
    ['door.held_open', 'Door held open'],
  ]);
  const match = known.get(type);
  if (match) return match;
  const words = type.replace(/[._-]+/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function eventTone(event: JourneyEvent): BadgeTone {
  const severity = event.severity.toLowerCase();
  const type = event.type.toLowerCase();
  if (severity === 'error' || severity === 'critical' || type.includes('denied') || type.includes('forced')) {
    return 'danger';
  }
  if (severity === 'warning' || severity === 'warn' || type.includes('held')) return 'warning';
  if (type.includes('authorized') || type.includes('unlock')) return 'success';
  return 'neutral';
}

function eventIcon(event: JourneyEvent): string {
  const type = event.type.toLowerCase();
  if (type.includes('denied')) return 'block';
  if (type.includes('forced') || type.includes('held')) return 'warning';
  if (type.includes('unlock') || type.includes('authorized')) return 'login';
  if (type.includes('lock')) return 'lock';
  if (type.includes('door')) return 'door_open';
  return 'event';
}

function locationLabel(event: JourneyEvent): string {
  if (event.door_name?.trim()) return event.door_name.trim();
  if (event.unifi_door_id) return `Unknown door (${event.unifi_door_id})`;
  if (event.unifi_device_id) return `Unknown reader (${event.unifi_device_id})`;
  return 'Unknown location';
}

function employeeDisplayName(employee: EmployeeOption | null | undefined): string {
  if (!employee) return 'Unknown person';
  return employee.tenant_name ? `${employee.tenant_name} - ${employee.name}` : employee.name;
}

function eventEmployee(event: JourneyEvent, selectedEmployee: EmployeeOption | null): EmployeeOption | null {
  return event.employee ?? selectedEmployee;
}

function captureAlt(employee: EmployeeOption | null, event: JourneyEvent): string {
  const who = employee ? ` for ${employeeDisplayName(employee)}` : '';
  return `Camera capture${who} at ${locationLabel(event)}`;
}

function NoImageBox({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center bg-surface-container text-on-surface-variant ${className}`}>
      <span className="font-label-md text-label-md">No Image</span>
    </div>
  );
}

function rangeLabel(fromDate: string, toDate: string): string {
  if (!fromDate && !toDate) return 'All recorded events';
  if (fromDate && toDate) return `${fromDate} to ${toDate}`;
  if (fromDate) return `Since ${fromDate}`;
  return `Through ${toDate}`;
}

function csvCell(value: string | null | undefined): string {
  const text = value ?? '';
  if (!/[",\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadJourneyCsv(
  scopeLabel: string,
  events: JourneyEvent[],
  selectedEmployee: EmployeeOption | null,
) {
  const header = ['Occurred at', 'Tenant', 'Employee', 'Email', 'Event', 'Severity', 'Location', 'Snapshot'];
  const rows = events.map((event) => [
    new Date(event.occurred_at).toISOString(),
    eventEmployee(event, selectedEmployee)?.tenant_name ?? '',
    eventEmployee(event, selectedEmployee)?.name ?? '',
    eventEmployee(event, selectedEmployee)?.email ?? '',
    eventLabel(event.type),
    event.severity,
    locationLabel(event),
    event.snapshot ? 'yes' : 'no',
  ]);
  const csv = [header, ...rows]
    .map((row) => row.map((cell) => csvCell(cell)).join(','))
    .join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${scopeLabel.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()}-journey.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function JourneysPage() {
  const { state } = useAuth();
  const { tenantId, tenantName } = useTenantScope();
  const isMaster = state.status === 'authed' && state.user.role === 'master_admin';
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantsLoaded, setTenantsLoaded] = useState(!isMaster);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(tenantId);
  const [employees, setEmployees] = useState<EmployeeOption[] | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(ALL_EMPLOYEES);
  const [events, setEvents] = useState<JourneyEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [personSearch, setPersonSearch] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [cameraFilter, setCameraFilter] = useState<CameraFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [rangePreset, setRangePreset] = useState<RangePreset>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState('100');

  useEffect(() => {
    if (tenantId) setSelectedTenantId(tenantId);
  }, [tenantId]);

  useEffect(() => {
    if (!isMaster) {
      setTenantsLoaded(true);
      return;
    }
    let cancelled = false;
    setTenantsLoaded(false);
    apiClient
      .get<{ data: TenantOption[] }>('/api/v1/tenants', { skipTenantScope: true })
      .then((res) => {
        if (cancelled) return;
        setTenants(res.data);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setTenantsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isMaster]);

  const applyRangePreset = useCallback((preset: RangePreset) => {
    setRangePreset(preset);
    if (preset === 'all' || preset === 'custom') {
      if (preset === 'all') {
        setFromDate('');
        setToDate('');
      }
      return;
    }
    const now = new Date();
    const from = new Date(now);
    if (preset === '24h') from.setDate(now.getDate() - 1);
    if (preset === '7d') from.setDate(now.getDate() - 7);
    if (preset === '30d') from.setDate(now.getDate() - 30);
    setFromDate(dateInputValue(from));
    setToDate(dateInputValue(now));
  }, []);

  useEffect(() => {
    const effectiveTenant = isMaster ? selectedTenantId : tenantId;
    if (isMaster && !effectiveTenant && !tenantsLoaded) {
      setEmployees(null);
      setEvents(null);
      setSelectedEmployeeId(ALL_EMPLOYEES);
      return;
    }
    let cancelled = false;
    setEmployees(null);
    setSelectedEmployeeId(ALL_EMPLOYEES);
    setEvents(null);
    setError(null);

    const request = isMaster && !effectiveTenant
      ? Promise.all(
          tenants.map((tenant) =>
            getAllEmployees<EmployeeOption>(tenantInit(tenant.id, true), 200)
              .then((res) =>
                res.data.map((employee) => ({
                  ...employee,
                  tenant_id: tenant.id,
                  tenant_name: tenant.name,
                })),
              ),
          ),
        ).then((groups) =>
          groups.flat().sort((a, b) => employeeDisplayName(a).localeCompare(employeeDisplayName(b))),
        )
      : effectiveTenant
        ? getAllEmployees<EmployeeOption>(tenantInit(effectiveTenant, isMaster), 200)
            .then((res) => {
              const tenant = isMaster ? tenants.find((item) => item.id === effectiveTenant) : null;
              return res.data.map((employee) => ({
                ...employee,
                tenant_id: employee.tenant_id ?? effectiveTenant,
                tenant_name: employee.tenant_name ?? tenant?.name ?? tenantName ?? null,
              }));
            })
        : Promise.resolve([] as EmployeeOption[]);

    request
      .then((res) => {
        if (cancelled) return;
        setEmployees(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load employees.');
          setEmployees([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isMaster, selectedTenantId, tenantId, tenantName, tenants, tenantsLoaded]);

  useEffect(() => {
    const effectiveTenant = isMaster ? selectedTenantId : tenantId;
    if ((isMaster && !effectiveTenant && !tenantsLoaded) || (!isMaster && !effectiveTenant)) {
      setEvents(null);
      return;
    }
    const params = new URLSearchParams({ limit });
    if (selectedEmployeeId !== ALL_EMPLOYEES) params.set('employee_id', selectedEmployeeId);
    const from = startOfLocalDate(fromDate);
    const to = endOfLocalDate(toDate);
    if (from) params.set('from', from);
    if (to) params.set('to', to);

    let cancelled = false;
    setEvents(null);
    setError(null);
    apiClient
      .get<{ data: { events: JourneyEvent[] } }>(
        `/api/v1/events/journey?${params.toString()}`,
        tenantInit(effectiveTenant, isMaster),
      )
      .then((res) => {
        if (!cancelled) setEvents(res.data.events);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load journey.');
          setEvents([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fromDate, isMaster, limit, selectedEmployeeId, selectedTenantId, tenantId, tenantsLoaded, toDate]);

  const effectiveTenantId = isMaster ? selectedTenantId : tenantId;
  const selectedTenantName =
    isMaster && !selectedTenantId
      ? 'All companies'
      : tenants.find((tenant) => tenant.id === selectedTenantId)?.name ?? tenantName ?? 'Selected company';
  const selectedEmployee = useMemo(
    () =>
      selectedEmployeeId === ALL_EMPLOYEES
        ? null
        : (employees ?? []).find((employee) => employee.id === selectedEmployeeId) ?? null,
    [employees, selectedEmployeeId],
  );
  const selectedScopeLabel = selectedEmployee
    ? employeeDisplayName(selectedEmployee)
    : `${selectedTenantName} all employees`;
  const filteredEmployees = useMemo(() => {
    const people = employees ?? [];
    const q = personSearch.trim().toLowerCase();
    if (!q) return people;
    return people.filter(
      (employee) =>
        employee.name.toLowerCase().includes(q) ||
        employee.email.toLowerCase().includes(q) ||
        (employee.tenant_name ?? '').toLowerCase().includes(q) ||
        employee.status.toLowerCase().includes(q),
    );
  }, [employees, personSearch]);

  const visibleEvents = useMemo(() => {
    const q = eventSearch.trim().toLowerCase();
    return (events ?? [])
      .filter((event) => {
        const tone = eventTone(event);
        if (severityFilter !== 'all' && tone !== severityFilter) return false;
        if (cameraFilter === 'captured' && !event.snapshot) return false;
        if (cameraFilter === 'missing' && event.snapshot) return false;
        if (!q) return true;
        return [
          event.type,
          eventLabel(event.type),
          event.severity,
          locationLabel(event),
          employeeDisplayName(eventEmployee(event, selectedEmployee)),
          eventEmployee(event, selectedEmployee)?.email ?? '',
          event.unifi_device_id ?? '',
          event.unifi_door_id ?? '',
        ]
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => {
        const diff = new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime();
        return sortOrder === 'newest' ? diff : -diff;
      });
  }, [cameraFilter, eventSearch, events, selectedEmployee, severityFilter, sortOrder]);

  const stats = useMemo(() => {
    const uniqueLocations = new Set(visibleEvents.map(locationLabel));
    const attention = visibleEvents.filter((event) => eventTone(event) === 'danger' || eventTone(event) === 'warning').length;
    return {
      total: visibleEvents.length,
      attention,
      snapshots: visibleEvents.filter((event) => event.snapshot).length,
      locations: uniqueLocations.size,
      people: selectedEmployee
        ? 1
        : new Set(
            visibleEvents
              .map((event) => eventEmployee(event, selectedEmployee)?.id)
              .filter((id): id is string => typeof id === 'string' && id.length > 0),
          ).size,
    };
  }, [selectedEmployee, visibleEvents]);
  const capturedEvents = visibleEvents.filter((event) => event.snapshot);
  const latestCapture = capturedEvents.reduce<JourneyEvent | null>((latest, event) => {
    if (!latest) return event;
    return new Date(event.occurred_at).getTime() > new Date(latest.occurred_at).getTime() ? event : latest;
  }, null);
  const snapshotEvents = capturedEvents.slice(0, 8);
  const cameraLocationCount = new Set(capturedEvents.map(locationLabel)).size;
  const loadingPeople = employees === null;
  const loadingJourney = events === null;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary-container text-on-primary">
            <Icon name="timeline" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">Employee journeys</h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Access history, reader photos, and location sequence by person or across a whole site.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => downloadJourneyCsv(selectedScopeLabel, visibleEvents, selectedEmployee)}
          disabled={visibleEvents.length === 0}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-surface-container px-4 py-2 font-label-md text-label-md text-on-surface hover:bg-surface-container-high disabled:opacity-50"
        >
          <Icon name="download" />
          <span>Export CSV</span>
        </button>
      </header>

      {error ? (
        <div role="alert" className="rounded-lg bg-error-container px-4 py-3 text-on-error-container">
          <Icon name="error" /> {error}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,360px)_1fr]">
        <aside className="min-w-0 space-y-4">
          <section className="min-w-0 rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
            <div className="min-w-0 space-y-4">
              {isMaster ? (
                <label
                  // A <select> sizes itself to its longest <option>, so one
                  // long company name set the width of this whole column and
                  // pushed the page off screen at every viewport — 60px over
                  // even at 768. min-w-0 lets the grid item shrink.
                  className="grid min-w-0 gap-1"
                >
                  <span className="font-label-md text-label-md text-on-surface-variant">Company</span>
                  <select
                    value={selectedTenantId ?? ''}
                    onChange={(e) => {
                      setSelectedTenantId(e.target.value || null);
                      setSelectedEmployeeId(ALL_EMPLOYEES);
                    }}
                    className="w-full min-w-0 rounded-lg border border-outline bg-surface px-3 py-2 font-body-md text-body-md"
                  >
                    <option value="">All companies</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <SearchInput
                value={personSearch}
                onChange={setPersonSearch}
                placeholder="Search employees"
                ariaLabel="Search employees"
              />
            </div>
          </section>

          <section className="overflow-hidden rounded-lg border border-outline-variant bg-surface-container-lowest">
            <div className="border-b border-outline-variant px-4 py-3">
              <h2 className="font-title-md text-title-md text-on-surface">Employees</h2>
              <p className="font-body-sm text-body-sm text-on-surface-variant">
                {selectedTenantName}
              </p>
            </div>
            {loadingPeople ? (
              <LoadingState label="Loading employees" className="flex justify-center p-8" />
            ) : (employees ?? []).length === 0 ? (
              <div className="p-6 font-body-md text-body-md text-on-surface-variant">
                No employees found for this scope.
              </div>
            ) : (
              <div className="max-h-[520px] overflow-y-auto divide-y divide-outline-variant">
                <button
                  type="button"
                  onClick={() => setSelectedEmployeeId(ALL_EMPLOYEES)}
                  className={`w-full px-4 py-3 text-left transition-colors ${
                    selectedEmployeeId === ALL_EMPLOYEES
                      ? 'bg-primary-container text-on-primary-container'
                      : 'hover:bg-surface-container'
                  }`}
                >
                  <span className="block font-body-md text-body-md font-medium">All employees</span>
                  <span className="block font-body-sm text-body-sm opacity-75">
                    {(employees ?? []).length} people · {selectedTenantName}
                  </span>
                </button>
                {filteredEmployees.length === 0 ? (
                  <div className="p-4 font-body-md text-body-md text-on-surface-variant">
                    No matching employees found.
                  </div>
                ) : null}
                {filteredEmployees.slice(0, 75).map((employee) => (
                  <button
                    key={employee.id}
                    type="button"
                    onClick={() => setSelectedEmployeeId(employee.id)}
                    className={`w-full px-4 py-3 text-left transition-colors ${
                      employee.id === selectedEmployeeId
                        ? 'bg-primary-container text-on-primary-container'
                        : 'hover:bg-surface-container'
                    }`}
                  >
                    <span className="block font-body-md text-body-md font-medium">
                      {employee.name}
                    </span>
                    {employee.tenant_name ? (
                      <span className="block truncate font-body-sm text-body-sm opacity-75">
                        {employee.tenant_name}
                      </span>
                    ) : null}
                    <span className="block truncate font-body-sm text-body-sm opacity-75">
                      {employee.email}
                    </span>
                    <span className="mt-2 flex flex-wrap gap-2">
                      <Badge tone={employee.status === 'active' ? 'success' : 'neutral'}>
                        {employee.status}
                      </Badge>
                      {!employee.unifi_user_id ? <Badge tone="warning">Not synced</Badge> : null}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </aside>

        <main className="space-y-4">
          <section className="rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
            <div className="grid gap-3 xl:grid-cols-[1fr_auto] xl:items-end">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <label className="grid gap-1">
                  <span className="font-label-md text-label-md text-on-surface-variant">From</span>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setRangePreset('custom');
                      setFromDate(e.target.value);
                    }}
                    className="rounded-lg border border-outline bg-surface px-3 py-2"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="font-label-md text-label-md text-on-surface-variant">To</span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => {
                      setRangePreset('custom');
                      setToDate(e.target.value);
                    }}
                    className="rounded-lg border border-outline bg-surface px-3 py-2"
                  />
                </label>
                <label className="grid gap-1">
                  <span className="font-label-md text-label-md text-on-surface-variant">Severity</span>
                  <select
                    value={severityFilter}
                    onChange={(e) => setSeverityFilter(e.target.value)}
                    className="rounded-lg border border-outline bg-surface px-3 py-2"
                  >
                    <option value="all">All events</option>
                    <option value="danger">Denied / critical</option>
                    <option value="warning">Warnings</option>
                    <option value="success">Granted</option>
                    <option value="neutral">Other</option>
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className="font-label-md text-label-md text-on-surface-variant">Rows</span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    className="rounded-lg border border-outline bg-surface px-3 py-2"
                  >
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className="font-label-md text-label-md text-on-surface-variant">Images</span>
                  <select
                    value={cameraFilter}
                    onChange={(e) => setCameraFilter(e.target.value as CameraFilter)}
                    className="rounded-lg border border-outline bg-surface px-3 py-2"
                  >
                    <option value="all">All events</option>
                    <option value="captured">With image</option>
                    <option value="missing">No Image</option>
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {(['24h', '7d', '30d', 'all'] as RangePreset[]).map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => applyRangePreset(preset)}
                    className={`rounded-lg border px-3 py-2 font-label-md text-label-md ${
                      rangePreset === preset
                        ? 'border-primary bg-primary-container text-on-primary-container'
                        : 'border-outline-variant bg-surface text-on-surface'
                    }`}
                  >
                    {preset === 'all' ? 'All' : preset}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <SearchInput
                value={eventSearch}
                onChange={setEventSearch}
                placeholder="Filter by door, reader, or event"
                ariaLabel="Filter journey events"
              />
            </div>
          </section>

          <section className="rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="font-title-lg text-title-lg text-on-surface">
                  {selectedEmployee ? selectedEmployee.name : 'All employees'}
                </h2>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  {selectedEmployee
                    ? `${selectedEmployee.email} · ${rangeLabel(fromDate, toDate)}`
                    : `${selectedTenantName} · ${rangeLabel(fromDate, toDate)}`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedEmployee ? (
                  <>
                    <Badge tone={selectedEmployee.status === 'active' ? 'success' : 'neutral'}>
                      {selectedEmployee.status}
                    </Badge>
                    {!selectedEmployee.unifi_user_id ? <Badge tone="warning">Not synced to UniFi</Badge> : null}
                  </>
                ) : (
                  <Badge tone="info">{(employees ?? []).length} people in scope</Badge>
                )}
              </div>
            </div>
          </section>

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <StatTile icon="event_note" label="Events" value={stats.total.toString()} />
            <StatTile icon="groups" label="People" value={stats.people.toString()} />
            <StatTile icon="priority_high" label="Needs review" value={stats.attention.toString()} tone={stats.attention > 0 ? 'danger' : 'success'} />
            <StatTile icon="photo_camera" label="Images" value={stats.snapshots.toString()} />
            <StatTile icon="pin_drop" label="Locations" value={stats.locations.toString()} />
          </section>

          {loadingJourney ? <LoadingState label="Loading journey" /> : null}

          {!loadingJourney && selectedEmployee && !selectedEmployee.unifi_user_id ? (
            <section className="rounded-lg border border-outline-variant bg-surface-container-lowest p-6 text-on-surface-variant">
              This employee is not synced to UniFi yet, so access events cannot be matched.
            </section>
          ) : null}

          {!loadingJourney && visibleEvents.length === 0 && (!selectedEmployee || selectedEmployee.unifi_user_id) ? (
            <section className="rounded-lg border border-outline-variant bg-surface-container-lowest p-6 text-on-surface-variant">
              No journey events match the current filters.
            </section>
          ) : null}

          {!loadingJourney && visibleEvents.length > 0 ? (
            <section className="grid gap-4 lg:grid-cols-[minmax(0,420px)_1fr]">
              <div className="overflow-hidden rounded-lg border border-outline-variant bg-surface-container-lowest">
                <div className="border-b border-outline-variant px-4 py-3">
                  <h3 className="font-title-md text-title-md text-on-surface">Latest image</h3>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    Most recent camera capture attached to this journey scope.
                  </p>
                </div>
                {latestCapture?.snapshot ? (
                  <Link to={`/events/${latestCapture.id}`} className="group block">
                    <img
                      src={snapshotSrc(latestCapture.snapshot.id, latestCapture.tenant_id ?? effectiveTenantId, isMaster)}
                      alt={captureAlt(eventEmployee(latestCapture, selectedEmployee), latestCapture)}
                      className="h-80 w-full bg-surface-container object-cover"
                    />
                    <div className="space-y-2 p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={eventTone(latestCapture)}>{eventLabel(latestCapture.type)}</Badge>
                        <Badge tone="info">Image</Badge>
                      </div>
                      <p className="font-body-md text-body-md text-on-surface">
                        {locationLabel(latestCapture)}
                      </p>
                      {!selectedEmployee ? (
                        <p className="font-body-sm text-body-sm text-on-surface">
                          {employeeDisplayName(eventEmployee(latestCapture, selectedEmployee))}
                        </p>
                      ) : null}
                      <p className="font-body-sm text-body-sm text-on-surface-variant">
                        {formatDateTime(latestCapture.occurred_at)}
                      </p>
                    </div>
                  </Link>
                ) : (
                  <NoImageBox className="h-80" />
                )}
              </div>

              <div className="rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="font-title-md text-title-md text-on-surface">Image trail</h3>
                    <p className="font-body-sm text-body-sm text-on-surface-variant">
                      {capturedEvents.length} of {visibleEvents.length} visible events include an image.
                    </p>
                  </div>
                  <Badge tone={capturedEvents.length > 0 ? 'info' : 'neutral'}>
                    {cameraLocationCount} image location{cameraLocationCount === 1 ? '' : 's'}
                  </Badge>
                </div>
                {snapshotEvents.length > 0 ? (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {snapshotEvents.map((event) => (
                      <Link
                        key={event.id}
                        to={`/events/${event.id}`}
                        className="group overflow-hidden rounded-lg border border-outline-variant bg-surface hover:border-primary"
                      >
                        <img
                          src={snapshotSrc(event.snapshot!.id, event.tenant_id ?? effectiveTenantId, isMaster)}
                          alt={captureAlt(eventEmployee(event, selectedEmployee), event)}
                          className="h-32 w-full bg-surface-container object-cover"
                        />
                        <div className="p-3">
                          <p className="truncate font-body-md text-body-md text-on-surface">
                            {locationLabel(event)}
                          </p>
                          <p className="font-body-sm text-body-sm text-on-surface-variant">
                            {formatDateTime(event.occurred_at)}
                          </p>
                          {!selectedEmployee ? (
                            <p className="truncate font-body-sm text-body-sm text-on-surface">
                              {employeeDisplayName(eventEmployee(event, selectedEmployee))}
                            </p>
                          ) : null}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <NoImageBox className="h-40 rounded-lg" />
                )}
              </div>
            </section>
          ) : null}

          {visibleEvents.length > 0 ? (
            <section className="rounded-lg border border-outline-variant bg-surface-container-lowest">
              <div className="flex flex-col gap-3 border-b border-outline-variant px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-title-md text-title-md text-on-surface">Timeline</h3>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    {visibleEvents.length} visible event{visibleEvents.length === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="inline-flex rounded-lg border border-outline-variant bg-surface p-1">
                  <button
                    type="button"
                    onClick={() => setSortOrder('newest')}
                    className={`rounded-md px-3 py-1.5 font-label-md text-label-md ${
                      sortOrder === 'newest' ? 'bg-primary-container text-on-primary-container' : 'text-on-surface'
                    }`}
                  >
                    Newest
                  </button>
                  <button
                    type="button"
                    onClick={() => setSortOrder('oldest')}
                    className={`rounded-md px-3 py-1.5 font-label-md text-label-md ${
                      sortOrder === 'oldest' ? 'bg-primary-container text-on-primary-container' : 'text-on-surface'
                    }`}
                  >
                    Oldest
                  </button>
                </div>
              </div>
              <ol className="divide-y divide-outline-variant">
                {visibleEvents.map((event) => (
                  <li key={event.id} className="grid gap-4 p-4 md:grid-cols-[auto_1fr_auto] md:items-start">
                    <div className="flex items-center gap-3 md:block">
                      <span
                        className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${
                          eventTone(event) === 'danger'
                            ? 'bg-error-container text-on-error-container'
                            : eventTone(event) === 'warning'
                              ? 'bg-tertiary-fixed text-on-tertiary-fixed'
                              : 'bg-primary-container text-on-primary-container'
                        }`}
                      >
                        <Icon name={eventIcon(event)} />
                      </span>
                      <span className="font-label-md text-label-md text-on-surface-variant md:hidden">
                        {relativeTime(event.occurred_at)}
                      </span>
                    </div>
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/events/${event.id}`} className="font-title-sm text-title-sm text-primary hover:underline">
                          {eventLabel(event.type)}
                        </Link>
                        <Badge tone={eventTone(event)}>{event.severity}</Badge>
                        {event.snapshot ? <Badge tone="info">Photo</Badge> : null}
                      </div>
                      <div className="font-body-md text-body-md text-on-surface">
                        {locationLabel(event)}
                      </div>
                      {!selectedEmployee ? (
                        <div className="font-body-sm text-body-sm text-on-surface">
                          {employeeDisplayName(eventEmployee(event, selectedEmployee))}
                          {eventEmployee(event, selectedEmployee)?.email
                            ? ` · ${eventEmployee(event, selectedEmployee)?.email}`
                            : ''}
                        </div>
                      ) : null}
                      <div className="font-body-sm text-body-sm text-on-surface-variant">
                        {new Date(event.occurred_at).toLocaleString()}
                      </div>
                    </div>
                    {event.snapshot ? (
                      <Link to={`/events/${event.id}`} className="block w-full md:w-36">
                        <img
                          src={snapshotSrc(event.snapshot.id, event.tenant_id ?? effectiveTenantId, isMaster)}
                          alt={captureAlt(eventEmployee(event, selectedEmployee), event)}
                          className="h-24 w-full rounded-lg bg-surface-container object-cover"
                        />
                      </Link>
                    ) : (
                      <div className="hidden md:block">
                        <NoImageBox className="h-24 w-36 rounded-lg" />
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </main>
      </div>
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  tone = 'neutral',
}: {
  icon: string;
  label: string;
  value: string;
  tone?: 'neutral' | 'success' | 'danger';
}) {
  const iconClass =
    tone === 'danger'
      ? 'bg-error-container text-on-error-container'
      : tone === 'success'
        ? 'bg-secondary-container text-on-secondary-container'
        : 'bg-primary-container text-on-primary-container';
  return (
    <div className="rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
      <div className="flex items-center gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${iconClass}`}>
          <Icon name={icon} />
        </span>
        <div>
          <p className="font-label-md text-label-md text-on-surface-variant">{label}</p>
          <p className="font-title-lg text-title-lg text-on-surface">{value}</p>
        </div>
      </div>
    </div>
  );
}
